import { makeStyles, tokens } from "@fluentui/react-components";
import { customScrollbarStyles } from "@/styles/scrollbar";
import { ottoTokens } from "@/theme/ottoTokens";

export const WorkflowRunsPanelStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  filterHeader: {
    // Reset native <button> defaults
    appearance: 'none',
    border: 'none',
    width: '100%',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalS,
    marginInline: `-${tokens.spacingHorizontalM}`,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    userSelect: 'none',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  filterHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  filterHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  filterIcon: {
    color: tokens.colorNeutralForeground3,
  },
  filterBadge: {
    marginLeft: tokens.spacingHorizontalXS,
  },
  filterChevron: {
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  activeFiltersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalXS,
  },
  activeFilterTag: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `2px ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  clearFiltersButton: {
    marginLeft: 'auto',
  },

  // Filter section (collapsible content)
  filterSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalM,
    marginInline: `-${tokens.spacingHorizontalM}`,
    paddingInline: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    animationName: {
      '0%': { opacity: 0, transform: 'translateY(-8px)' },
      '100%': { opacity: 1, transform: 'translateY(0)' },
    },
    animationDuration: tokens.durationFast,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'forwards',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
    },
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  filterLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },

  // Status filter - checkbox style
  statusOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  statusOption: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transitionProperty: 'background-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':hover': {
        transform: 'none',
      },
    },
  },
  statusOptionSelected: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  statusCheckbox: {
    width: tokens.spacingHorizontalL,
    height: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  statusCheckboxChecked: {
    backgroundColor: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  statusDot: {
    width: tokens.spacingHorizontalS,
    height: tokens.spacingHorizontalS,
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusDotSuccess: {
    backgroundColor: ottoTokens.statusSucceeded,
  },
  statusDotFailed: {
    backgroundColor: ottoTokens.statusFailed,
  },
  statusDotRunning: {
    backgroundColor: ottoTokens.statusRunning,
  },
  statusDotCancelled: {
    backgroundColor: ottoTokens.statusCancelled,
  },
  statusText: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },

  // Search input
  searchInput: {
    width: '100%',
  },

  // Time range - segmented control
  segmentedControl: {
    display: 'flex',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalXXS,
    gap: tokens.spacingHorizontalXXS,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  segment: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  segmentActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  customTimeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  customTimeInput: {
    width: '70px',
  },
  customTimeSelect: {
    minWidth: '80px',
  },

  // Divider between filter and list
  divider: {
    height: tokens.spacingVerticalS,
  },

  // Header with count and refresh
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: tokens.spacingVerticalS,
  },
  runCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  runCountNumber: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    border: 'none',
    backgroundColor: 'transparent',
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    cursor: 'pointer',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    ':disabled': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },

  // Runs list
  runsList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    marginInline: `-${tokens.spacingHorizontalM}`,
    ...customScrollbarStyles,
  },
  runCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderLeft: '3px solid transparent',
    transitionProperty: 'background-color, border-left-color, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':hover': {
        transform: 'none',
      },
    },
  },
  runCardSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: tokens.colorBrandStroke1,
    transform: 'none',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
      transform: 'none',
    },
  },
  runCardFailed: {
    borderLeftColor: ottoTokens.statusFailed,
  },
  runCardFocused: {
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '-2px',
  },
  runStatusIndicator: {
    width: tokens.spacingHorizontalMNudge,
    height: tokens.spacingHorizontalMNudge,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground1}`,
  },
  runStatusSuccess: {
    backgroundColor: ottoTokens.statusSucceeded,
  },
  runStatusFailed: {
    backgroundColor: ottoTokens.statusFailed,
  },
  runStatusRunning: {
    backgroundColor: ottoTokens.statusRunning,
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground1}, 0 0 8px ${ottoTokens.statusRunning}`,
  },
  runStatusCancelled: {
    backgroundColor: ottoTokens.statusCancelled,
  },
  runInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  runPrimaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  runTime: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  runMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  runDuration: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    fontFamily: 'monospace',
  },
  draftIndicator: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  draftIcon: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground2,
  },

  // Empty state - modern card-like design
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  emptyStateCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    paddingInline: tokens.spacingHorizontalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    maxWidth: '260px',
  },
  emptyIconContainer: {
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusCircular,
    marginBottom: tokens.spacingVerticalXS,
  },
  emptyIcon: {
    fontSize: tokens.fontSizeHero700,
    color: tokens.colorNeutralForeground3,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  emptySubtitle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase200,
    margin: 0,
  },
  emptyActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },

  // Loading state
  loadingState: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Skeleton loading
  skeletonCard: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  skeletonPulse: {
    animationName: {
      from: { opacity: 0.4 },
      to: { opacity: 0.7 },
    },
    animationDuration: tokens.durationUltraSlow,
    animationTimingFunction: tokens.curveEasyEase,
    animationIterationCount: 'infinite',
    animationDirection: 'alternate',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      animationDuration: '0.01ms',
      opacity: 0.7,
    },
  },
  skeletonDot: {
    width: tokens.spacingHorizontalMNudge,
    height: tokens.spacingHorizontalMNudge,
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralStroke1,
    flexShrink: 0,
  },
  skeletonContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalSNudge,
  },
  skeletonLine: {
    height: '12px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke1,
  },
  skeletonLinePrimary: {
    width: '60%',
    height: '14px',
  },
  skeletonLineSecondary: {
    width: '40%',
  },
  skeletonDuration: {
    width: '50px',
    height: '14px',
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralStroke1,
  },

  // Loading more indicator
  loadMoreIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  loadMoreSentinel: {
    height: '1px',
  },
  activeFiltersRowPadding: {
    padding: `0 ${tokens.spacingHorizontalM}`,
    paddingBottom: tokens.spacingVerticalS,
  },
  lastLabel: {
    color: tokens.colorNeutralForeground3,
  },
  refreshIconSpinning: {
    animationName: {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    animationDuration: tokens.durationUltraSlow,
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
      animationIterationCount: 1,
    },
  },
});